import React, { useState } from 'react';
import { Modal, Form, Switch, InputNumber, message, Typography, Divider } from 'antd';
import { adminTasksApi } from '../../../services/api/adminTasks';
import { AdminTaskType } from '../../../types';

const { Text } = Typography;

interface AutoEpisodeConfigModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  storyId: string;
  processId: string;
  processName: string;
}

const AutoEpisodeConfigModal: React.FC<AutoEpisodeConfigModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  storyId,
  processId,
  processName,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showHookSettings, setShowHookSettings] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const detail = {
        input: {
          sourceStoryId: storyId,
          processId: processId,
          generateHook: values.generateHook,
          hookPassScore: values.hookPassScore,
          maxHookRetries: values.maxHookRetries,
          renderVideo: values.renderVideo,
          enableBgm: values.enableBgm,
          enableSfx: values.enableSfx,
        },
        progress: 0,
        process: {},
        targetId: storyId,
      };

      await adminTasksApi.createTask({
        type: AdminTaskType.AUTO_EPISODE,
        detail: detail as any,
      });

      message.success(`Đã khởi tạo tiến trình Auto Episode cho "${processName}" thành công!`);
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to create auto episode task:', error);
      message.error('Không thể khởi tạo tiến trình: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span>🎬 Tự động tạo Episode - <b>{processName}</b></span>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={500}
      okText="Bắt đầu"
      cancelText="Hủy"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          generateHook: false,
          hookPassScore: 85,
          maxHookRetries: 5,
          renderVideo: true,
          enableBgm: false,
          enableSfx: false,
        }}
        onValuesChange={(changedValues) => {
          if (changedValues.generateHook !== undefined) {
            setShowHookSettings(changedValues.generateHook);
          }
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Hệ thống sẽ tự động tạo Episode mới, gộp toàn bộ Panel trong Process, tạo Voice-over, phân tích Timestamps và Render Video hoàn chỉnh.
          </Text>
        </div>

        <Divider orientation={"left" as any}>Cấu hình Hook (Mở đầu)</Divider>

        <Form.Item
          name="generateHook"
          label="Tự động tạo Hook mở đầu"
          valuePropName="checked"
          tooltip="Nếu bật, AI sẽ tự động phân tích và tạo đoạn Hook ấn tượng ở đầu video"
        >
          <Switch />
        </Form.Item>

        {showHookSettings && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="hookPassScore"
              label="Ngưỡng điểm Hook"
              tooltip="Điểm AI chấm để chấp nhận bản Hook"
            >
              <InputNumber min={1} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="maxHookRetries"
              label="Số lần thử tối đa"
            >
              <InputNumber min={1} max={10} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        )}

        <Text type="secondary" style={{ fontSize: '12px' }}>
          * Quá trình Render Video có thể mất từ 5-15 phút tùy độ dài Episode.
        </Text>

        <Divider orientation={"left" as any}>Tuỳ chọn Pipeline</Divider>

        <Form.Item
          name="renderVideo"
          label="Render Video cuối"
          valuePropName="checked"
          tooltip="Tắt nếu chỉ muốn test audio/hook, không cần render video"
        >
          <Switch />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="enableBgm"
            label="Tạo Background Music"
            valuePropName="checked"
            tooltip="Bật nếu muốn AI chọn nhạc nền. Nên tắt khi đăng TikTok"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="enableSfx"
            label="Tạo Sound Effects"
            valuePropName="checked"
            tooltip="Bật nếu muốn AI thêm hiệu ứng âm thanh vào từng panel"
          >
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default AutoEpisodeConfigModal;
